import { ChangeDetectorRef, Component, OnInit, Pipe, PipeTransform } from '@angular/core';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { MatDialog, MatSelect } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { Subject, forkJoin, of } from 'rxjs';
import { EquipmentCompatibleProductService, EquipmentModelService, BrandService } from '@app/api/api';
import { EquipmentTypeService } from '@app/api/equipmentType.service';
import { EquipmentCategoryService } from '@app/api/equipmentCategory.service';
import { environment } from 'environments/environment';
import { debounceTime, map } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { ModalCompatibleProductComponent } from '../modal-compatible-product/modal-compatible-product.component';

@Pipe({ name: 'mapPrefixCodes' })
export class MapPrefixCodesPipe implements PipeTransform {
  transform(prefixes: any[]): string {
    if (!prefixes || prefixes.length === 0) return '';
    return prefixes.map(p => p.prefix_code + (p.assembly_type ? ('/' + p.assembly_type) : '')).join(', ');
  }
}

export interface CompatibleProduct {
  id: string | number;
  extend_equipment_model?: {
    name?: string;
  };
  equipment_model_name?: string;
  equipment_model?: {
    name?: string;
  };
  extend_product?: {
    name?: string;
  };
  product_name?: string;
  product?: {
    name?: string;
  };
  compatibility_note?: string;
  is_recommended?: boolean;
  serial?: string;
  compatible_prefixes?: any[];
}

@Component({
  selector: 'meu-compatible-product-management',
  templateUrl: './compatible-product-management.component.html',
  styleUrls: ['./compatible-product-management.component.scss']
})
export class CompatibleProductManagementComponent implements OnInit {
  constructor(
    private layoutUtilsService: LayoutUtilsService,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private ref: ChangeDetectorRef,
    private compatibleProductService: EquipmentCompatibleProductService,
    private equipmentModelService: EquipmentModelService,
    private brandService: BrandService,
    private equipmentTypeService: EquipmentTypeService,
    private equipmentCategoryService: EquipmentCategoryService
  ) {}

  displayedColumns: string[] = [
    'Index',
    'ModelName',
    'ProductName',
    'Prefixes',
    'Serial',
    'CompatibilityNote',
    'IsRecommended',
    'Actions'
  ];
  lstAll: CompatibleProduct[] = [];
  lstModels: any[] = [];
  lstPrefixes: any[] = [];
  lstBrands: any[] = [];
  lstEquipmentCategories: any[] = [];
  lstEquipmentTypes: any[] = [];
  selectedModelId: string = 'all';
  selectedPrefixId: string = '';
  selectedBrandId: string = '';
  selectedCategoryId: string = '';
  selectedTypeId: string = '';
  total: number = 0;
  pageSize = 10;
  pageIndex = 1;
  host: string = '';
  filter: string = '';
  searchText: string = '';
  recommendedFilter: string = 'all';
  pageSizeOptions: number[] = [5, 10, 20, 100];

  search$ = new Subject<string>();

  modelSearchCtrl = new FormControl();
  prefixSearchCtrl = new FormControl();
  brandSearchCtrl = new FormControl();
  categorySearchCtrl = new FormControl();
  typeSearchCtrl = new FormControl();

  filteredModels: any[] = [];
  filteredPrefixes: any[] = [];
  filteredBrands: any[] = [];
  filteredCategories: any[] = [];
  filteredTypes: any[] = [];

  ngOnInit() {
    this.loadModels();
    this.loadBrands();
    this.loadEquipmentCategories();
    this.getAll();

    this.search$.pipe(debounceTime(500)).subscribe((val) => {
      this.searchText = val;
      this.pageIndex = 1;
      this.getAll();
    });

    this.modelSearchCtrl.valueChanges.subscribe((val: string) => {
      const search = (val || '').toLowerCase();
      this.filteredModels = this.lstModels.filter(m =>
        (m.model_code || '').toLowerCase().includes(search) || (m.name || '').toLowerCase().includes(search)
      );
    });
    this.prefixSearchCtrl.valueChanges.subscribe((val: string) => {
      const search = (val || '').toLowerCase();
      this.filteredPrefixes = this.lstPrefixes.filter(p =>
        (p.prefix_code || '').toLowerCase().includes(search)
      );
    });
    this.brandSearchCtrl.valueChanges.subscribe((val: string) => {
      const search = (val || '').toLowerCase();
      this.filteredBrands = this.lstBrands.filter(b =>
        (b.name || '').toLowerCase().includes(search)
      );
    });
    this.categorySearchCtrl.valueChanges.subscribe((val: string) => {
      const search = (val || '').toLowerCase();
      this.filteredCategories = this.lstEquipmentCategories.filter(c =>
        (c.name || '').toLowerCase().includes(search)
      );
    });
    this.typeSearchCtrl.valueChanges.subscribe((val: string) => {
      const search = (val || '').toLowerCase();
      this.filteredTypes = this.lstEquipmentTypes.filter(t =>
        (t.name || '').toLowerCase().includes(search)
      );
    });
  }

  loadModels() {
    this.equipmentModelService
      .apiEquipmentModelsGet('', '', 1, 1000)
      .subscribe((res) => {
        if (res.success && res.data && res.data.collection) {
          this.lstModels = res.data.collection;
          this.filteredModels = this.lstModels.slice();
          this.ref.detectChanges();
        }
      });
  }

  loadBrands() {
    this.brandService.apiBrandsGet('', '', 1, 1000).subscribe((res) => {
      if (res.success && res.data) {
        this.lstBrands = res.data.collection;
        this.filteredBrands = this.lstBrands.slice();
        this.ref.detectChanges();
      }
    });
  }

  loadEquipmentCategories() {
    this.equipmentCategoryService.apiEquipmentCategoriesGet('', '', 1, 1000).subscribe((res) => {
      if (res.success && res.data) {
        this.lstEquipmentCategories = res.data.collection;
        this.filteredCategories = this.lstEquipmentCategories.slice();
        this.ref.detectChanges();
      }
    });
  }

  loadEquipmentTypesByCategory(categoryId?: string) {
    if (!categoryId) {
      this.lstEquipmentTypes = [];
      this.filteredTypes = [];
      this.selectedTypeId = '';
      this.ref.detectChanges();
      return;
    }
    const filters = 'category_id==' + categoryId;
    this.equipmentTypeService.apiEquipmentTypesGet(filters, '', 1, 1000).subscribe((res) => {
      if (res.success && res.data) {
        this.lstEquipmentTypes = res.data.collection;
        this.filteredTypes = this.lstEquipmentTypes.slice();
        this.ref.detectChanges();
      }
    });
  }

  loadPrefixesByModel(modelId: string) {
    if (!modelId) {
      this.lstPrefixes = [];
      this.filteredPrefixes = [];
      this.selectedPrefixId = '';
      this.ref.detectChanges();
      return;
    }
    this.equipmentModelService.apiEquipmentModelsIdGet(modelId).subscribe((res) => {
      if (res.success && res.data) {
        this.lstPrefixes = res.data.prefixes || [];
        this.filteredPrefixes = this.lstPrefixes.slice();
        this.ref.detectChanges();
      }
    });
  }

  onModelFilterChange() {
    this.pageIndex = 1;
    this.selectedPrefixId = '';
    this.lstPrefixes = [];
    if (this.selectedModelId && this.selectedModelId !== 'all') {
      this.loadPrefixesByModel(this.selectedModelId);
      this.getAll();
    } else if (this.selectedModelId === 'all') {
      this.getAll();
    } else {
      this.lstAll = [];
      this.total = 0;
      this.ref.detectChanges();
    }
  }

  onPrefixFilterChange() {
    this.pageIndex = 1;
    this.getAll();
  }

  onBrandFilterChange() {
    this.pageIndex = 1;
    this.getAll();
  }

  onCategoryFilterChange() {
    this.pageIndex = 1;
    this.selectedTypeId = '';
    this.loadEquipmentTypesByCategory(this.selectedCategoryId);
    this.getAll();
  }

  onTypeFilterChange() {
    this.pageIndex = 1;
    this.getAll();
  }

  getAll() {
    if (!this.selectedModelId) {
      this.lstAll = [];
      this.total = 0;
      this.ref.detectChanges();
      return;
    }

    const isAll = this.selectedModelId === 'all';
    const modelIdParam = isAll ? undefined : this.selectedModelId;

    let filters: string[] = [];
    if (this.recommendedFilter === 'recommended') {
      filters.push('is_recommended==true');
    } else if (this.recommendedFilter === 'not_recommended') {
      filters.push('is_recommended==false');
    }
    const searchParam = this.searchText ? this.searchText.trim() : '';
    if (searchParam) {
      filters.push('(productName|equipmentModelName|equipmentModelPrefixName|serial)@=*' + searchParam);
    }
    const filtersParam = filters.length > 0 ? filters.join(',') : undefined;

    this.compatibleProductService
      .apiEquipmentCompatibleProductsGet(
        filtersParam,
        '-created_at',
        this.pageIndex,
        this.pageSize,
        modelIdParam,
        isAll ? undefined : (this.selectedPrefixId || undefined),
        this.selectedBrandId || undefined,
        this.selectedCategoryId || undefined,
        this.selectedTypeId || undefined
      )
      .subscribe((res) => {
        if (res.success) {
          this.lstAll = res.data.collection;
          this.total = res.data.total;
          this.host = environment.backendhost;
          this.ref.detectChanges();
        } else {
          this.toastr.error(res.message);
        }
      });
  }

  onRecommendedFilterChange(val: string) {
    this.recommendedFilter = val;
    this.pageIndex = 1;
    this.getAll();
  }

  onPageChange(pageNumber: number) {
    if (typeof pageNumber === 'number') {
      this.pageIndex = pageNumber;
      this.getAll();
      window.scrollTo(0, 0);
    }
  }

  onPageSizeChange(newSize: number) {
    this.pageSize = newSize;
    this.pageIndex = 1;
    this.getAll();
  }

  openModal(id: string | number) {
    const dialogRef = this.dialog.open(ModalCompatibleProductComponent, {
      width: '700px',
      data: { id: id }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != undefined && result.event != undefined) {
        if (result.event) {
          if (result.equipment_model_id && this.selectedModelId !== 'all') {
            this.selectedModelId = result.equipment_model_id;
            this.loadPrefixesByModel(this.selectedModelId);
          }
          if (result.type == 'create') {
            this.toastr.success('Thêm sản phẩm tương thích thành công');
            this.getAll();
          } else {
            this.toastr.success('Cập nhật sản phẩm tương thích thành công');
            this.getAll();
          }
        } else {
          this.toastr.error(result.message);
        }
      }
    });
  }

  deleteItem(id: string | number) {
    const title = 'Xóa sản phẩm tương thích';
    const description = 'Bạn có chắc chắn muốn xóa phần này?';
    const waitDesciption = 'Đang xử lý...';
    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      } else {
        this.compatibleProductService
          .apiEquipmentCompatibleProductsIdDelete(String(id))
          .subscribe((res) => {
            if (res.success) {
              this.toastr.success('Xoá sản phẩm tương thích thành công');
              this.getAll();
            } else {
              this.toastr.error(res.message);
            }
          });
      }
    });
  }
}
